import {
    VoiceAgentCallCategory,
    VoiceAgentSetupData,
    VoiceAgentSetupDataPatch,
    VoiceAgentTone,
} from '../../models/VoiceAgent'

export const SETUP_STEPS = ['Property', 'Agent', 'Documents', 'Number']

export const TONE_OPTIONS: {
    value: VoiceAgentTone
    label: string
    description: string
}[] = [
    {
        value: 'warm',
        label: 'Warm',
        description: 'Friendly and conversational',
    },
    {
        value: 'professional',
        label: 'Professional',
        description: 'Polished and clear',
    },
    {
        value: 'brisk',
        label: 'Brisk',
        description: 'Short and to the point',
    },
]

export type EditableFieldKey = Exclude<
    keyof VoiceAgentSetupDataPatch,
    'confirm'
>

export interface FieldDef {
    key: EditableFieldKey
    label: string
    placeholder?: string
    multiline?: boolean
    helper?: string
}

export const REQUIRED_FIELDS: FieldDef[] = []

export const OPTIONAL_FIELDS: FieldDef[] = [
    {
        key: 'unit_summary_stub',
        label: 'What to say about availability',
        placeholder:
            'e.g. I can have someone from the office follow up with current availability',
        helper:
            'Live unit-level availability is not on this line. Lease term and new and renewal rents still come from the current leasing season automatically.',
        multiline: true,
    },
    {
        key: 'unknown_answer_behavior',
        label: 'When the agent doesn\u2019t know',
        placeholder:
            'e.g. Offer to take a message and have the leasing team follow up',
        multiline: true,
    },
]

export const EDITABLE_FIELDS = [...REQUIRED_FIELDS, ...OPTIONAL_FIELDS]

export const formatE164 = (e164: string): string => {
    const digits = e164.replace(/\D/g, '')
    if (digits.length === 11 && digits.startsWith('1')) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(
            7,
        )}`
    }
    return e164
}

export const formatDuration = (sec: number): string => {
    if (!sec || sec < 0) return '0s'
    const m = Math.floor(sec / 60)
    const s = sec % 60
    return m > 0 ? `${m}m ${s}s` : `${s}s`
}

export const formatRelativeTime = (iso: string): string => {
    const diff = Date.now() - new Date(iso).getTime()
    const mins = Math.floor(diff / 60000)
    if (mins < 1) return 'Just now'
    if (mins < 60) return `${mins} min ago`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 7) return `${days}d ago`
    return formatCallDateTime(iso)
}

export const formatCallDateTime = (iso: string): string => {
    const d = new Date(iso)
    return d.toLocaleString(undefined, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    })
}

const ENDED_REASON_LABELS: Record<string, string> = {
    hangup: 'Caller hung up',
    'customer-ended-call': 'Caller hung up',
    'assistant-ended-call': 'Agent ended call',
    voicemail: 'Voicemail',
    'silence-timed-out': 'Silence timeout',
    'max-duration-reached': 'Max duration',
}

export const formatEndedReason = (reason: string): string => {
    if (!reason) return 'Unknown'
    if (ENDED_REASON_LABELS[reason]) return ENDED_REASON_LABELS[reason]
    return reason.replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

export const CALL_CATEGORY_META: Record<
    VoiceAgentCallCategory,
    { label: string; bg: string; color: string }
> = {
    LEASING: { label: 'Leasing', bg: '#e8eaf6', color: '#3949ab' },
    MAINTENANCE: { label: 'Maintenance', bg: '#e0f2f1', color: '#00796b' },
    GENERAL_SOLICITATION: {
        label: 'General solicitation',
        bg: '#fff8e1',
        color: '#f57f17',
    },
    '': { label: 'Uncategorized', bg: '#f5f5f5', color: '#757575' },
}

export const CALL_CATEGORY_TABS: {
    value: VoiceAgentCallCategory | 'ALL'
    label: string
}[] = [
    { value: 'ALL', label: 'All' },
    { value: 'LEASING', label: 'Leasing' },
    { value: 'MAINTENANCE', label: 'Maintenance' },
    { value: 'GENERAL_SOLICITATION', label: 'Solicitation' },
]

export const ALLOWED_DOC_EXTS = ['.txt', '.md', '.pdf', '.docx']
export const MAX_DOC_BYTES = 5 * 1024 * 1024 // 5 MB
export const MAX_DOC_FILES = 10
export const MAX_DOC_CHARS = 40_000

export const formatByteSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export const fieldsFromSetupData = (
    data: VoiceAgentSetupData,
): Record<EditableFieldKey, string> => {
    const initial = {} as Record<EditableFieldKey, string>
    EDITABLE_FIELDS.forEach(({ key }) => {
        initial[key] = data[key] ?? ''
    })
    return initial
}
